/**
 * useSystemLogs Hook
 *
 * Centralized hook for accessing all system log services (Application, API, Query)
 * Pattern: Similar to usePermissionManagement
 *
 * Note: Uses direct fetch for log endpoints as they return raw arrays instead of ResponseData<T>
 */

import { getApiUrl } from '../../../../config/ccms';
import { useEnrichedAuth } from '../../../../hooks/useEnrichedAuth';
import type { AppLogDto, ApiRequestLogDto, QueryPerformanceLogDto } from '@actschurch/ccms-sdk';
import type { DeadLetterResult } from '../types';

export function useSystemLogs() {
  const { user } = useEnrichedAuth();

  // Helper to build URL with query params
  const buildUrl = (path: string, params?: Record<string, string | number>) => {
    const baseUrl = getApiUrl('system', path);
    if (!params) return baseUrl;

    const queryString = Object.entries(params)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&');

    return `${baseUrl}?${queryString}`;
  };

  // Direct fetch wrapper that handles raw array responses
  const fetchRaw = async <T>(url: string): Promise<T> => {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    // Add authorization header if user is authenticated
    if (user?.accessToken) {
      headers['Authorization'] = `Bearer ${user.accessToken}`;
    }

    const response = await fetch(url, { headers });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  };

  return {
    // Application logs
    getRecentAppLogs: async (count: number = 100, serviceName?: string) => {
      const params: Record<string, string | number> = { count };
      if (serviceName) params.serviceName = serviceName;
      const url = buildUrl('/api/logs/recent', params);
      return fetchRaw<AppLogDto[]>(url);
    },

    getServices: async () => {
      const url = buildUrl('/api/logs/services');
      return fetchRaw<string[]>(url);
    },

    // API request logs
    getRecentApiLogs: async (count: number = 100, serviceName?: string) => {
      const params: Record<string, string | number> = { count };
      if (serviceName) params.serviceName = serviceName;
      const url = buildUrl('/api/apirequestlog/recent', params);
      return fetchRaw<ApiRequestLogDto[]>(url);
    },

    // Query performance logs
    getRecentQueryLogs: async (count: number = 100, serviceName?: string) => {
      const params: Record<string, string | number> = { count };
      if (serviceName) params.serviceName = serviceName;
      const url = buildUrl('/api/queryperformancelog/recent', params);
      return fetchRaw<QueryPerformanceLogDto[]>(url);
    },

    // Dead-letter queue — RabbitMQ messages that failed processing.
    // This endpoint wraps its payload in { success, message, data }.
    getDeadLetterMessages: async (count: number = 50): Promise<DeadLetterResult> => {
      const url = buildUrl('/api/rabbitmqmanagement/dead-letter', { count });
      const response = await fetchRaw<{ success: boolean; message: string; data: DeadLetterResult }>(url);
      return response.data;
    },
  };
}
